/**
 * Admin-only: the persona registry.
 *
 * A persona is a registered synthetic voice, an AI agent chiefly, that sits the
 * survey without voting: its stances are shown beside the team positions and
 * never counted in them. An admin registers who it is (key, name, the person
 * accountable for it, the one account allowed to voice it); the voice declares
 * what it runs on at the start of each session, shown here read-only and marked
 * as self-reported.
 */
import { useEffect, useState } from "react";
import { Bot, RotateCcw, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useAccessPeople, useCreatePersona, usePersonas, useSetPersonaRetired } from "@/lib/useAdmin";
import type { AccessPerson, Persona } from "@/lib/types";
import { cn } from "@/lib/utils";

function errorDetail(error: unknown, fallback: string): string {
  const detail = (error as { body?: { detail?: unknown } } | null)?.body?.detail;
  return typeof detail === "string" ? detail : fallback;
}

const OBJECT_ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** What the typed text can be used as without a search: the tenant search needs
 *  Graph permissions the app may not hold (the access panel says so), and
 *  registering a persona must not depend on them. */
function manualEntry(text: string, manual: "oid" | "email"): AccessPerson | null {
  const value = text.trim();
  if (manual === "oid" && OBJECT_ID.test(value)) {
    return { id: value.toLowerCase(), display_name: `Object id ${value.toLowerCase()}`, upn: null, kind: "user" };
  }
  if (manual === "email" && EMAIL.test(value)) {
    return { id: "", display_name: null, upn: value.toLowerCase(), kind: "user" };
  }
  return null;
}

/** Search the tenant and pick one person, or use what was typed when it is an
 *  object id (``manual="oid"``) or an address (``manual="email"``). */
function PersonPicker({
  id,
  label,
  picked,
  onPick,
  manual,
}: {
  id: string;
  label: string;
  picked: AccessPerson | null;
  onPick: (person: AccessPerson | null) => void;
  manual: "oid" | "email";
}) {
  const [query, setQuery] = useState("");
  const [debounced, setDebounced] = useState("");
  useEffect(() => {
    const handle = window.setTimeout(() => setDebounced(query), 250);
    return () => window.clearTimeout(handle);
  }, [query]);
  const people = useAccessPeople(picked ? "" : debounced);
  const typed = picked ? null : manualEntry(query, manual);

  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={id} className="font-sans text-xs font-semibold text-bw-navy">
        {label}
      </label>
      <input
        id={id}
        className="rounded-pill border border-bw-blue-muted/50 px-3 py-1.5 font-sans text-sm"
        placeholder={manual === "oid" ? "Name to search, or an Entra object id" : "Name to search, or an email address"}
        value={picked ? (picked.upn ?? picked.display_name ?? picked.id) : query}
        onChange={(event) => {
          onPick(null);
          setQuery(event.target.value);
        }}
        autoComplete="off"
      />
      {typed && (
        <Button type="button" variant="ghost" size="sm" className="w-fit" onClick={() => onPick(typed)}>
          Use {typed.upn ?? typed.id}
        </Button>
      )}
      {!picked && people.data && people.data.length > 0 && (
        <ul className="flex flex-col">
          {people.data.map((person) => (
            <li key={person.id}>
              <button
                type="button"
                className="flex w-full flex-col items-start rounded-card px-2 py-1 text-left hover:bg-bw-navy/5"
                onClick={() => onPick(person)}
              >
                <span className="font-sans text-sm text-bw-navy">{person.display_name ?? person.upn}</span>
                {person.upn && <span className="font-sans text-xs text-bw-ink-muted">{person.upn}</span>}
                {person.kind === "service_principal" && (
                  <span className="font-sans text-xs text-bw-ink-muted">Application (a headless agent's identity)</span>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function RegisterPersona() {
  const [key, setKey] = useState("");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [operator, setOperator] = useState<AccessPerson | null>(null);
  const [accountable, setAccountable] = useState<AccessPerson | null>(null);
  const create = useCreatePersona();
  const ready = key.trim() && name.trim() && operator && accountable?.upn;

  return (
    <form
      className="flex flex-col gap-3 rounded-card border border-bw-blue-muted/40 p-3"
      onSubmit={(event) => {
        event.preventDefault();
        if (!ready) return;
        create.mutate(
          {
            persona_key: key.trim().toLowerCase(),
            display_name: name.trim(),
            description: description.trim() || null,
            accountable_upn: accountable!.upn!,
            operator_oid: operator!.id,
            operator_upn: operator!.upn,
          },
          {
            onSuccess: () => {
              setKey("");
              setName("");
              setDescription("");
              setOperator(null);
              setAccountable(null);
            },
          },
        );
      }}
    >
      <span className="font-sans text-xs font-semibold uppercase tracking-wide text-bw-ink-muted">
        Register a persona
      </span>
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="flex flex-col gap-1 font-sans text-xs font-semibold text-bw-navy">
          Key
          <input
            className="rounded-pill border border-bw-blue-muted/50 px-3 py-1.5 font-sans text-sm font-normal"
            placeholder="hank"
            value={key}
            onChange={(e) => setKey(e.target.value)}
            maxLength={48}
          />
        </label>
        <label className="flex flex-col gap-1 font-sans text-xs font-semibold text-bw-navy">
          Name
          <input
            className="rounded-pill border border-bw-blue-muted/50 px-3 py-1.5 font-sans text-sm font-normal"
            placeholder="Hank"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={120}
          />
        </label>
      </div>
      <label className="flex flex-col gap-1 font-sans text-xs font-semibold text-bw-navy">
        What it is
        <input
          className="rounded-pill border border-bw-blue-muted/50 px-3 py-1.5 font-sans text-sm font-normal"
          placeholder="The autonomous dev agent in agentic-pm-workflow"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          maxLength={2000}
        />
      </label>
      <div className="grid gap-3 sm:grid-cols-2">
        <PersonPicker
          id="persona-operator"
          label="Account that voices it"
          picked={operator}
          onPick={setOperator}
          manual="oid"
        />
        <PersonPicker
          id="persona-accountable"
          label="Person accountable for it"
          picked={accountable}
          onPick={(person) => setAccountable(person && person.kind === "user" ? person : null)}
          manual="email"
        />
      </div>
      <div className="flex items-center gap-2">
        <Button type="submit" size="sm" disabled={!ready || create.isPending}>
          {create.isPending ? "Registering…" : "Register persona"}
        </Button>
        {create.isError && (
          <span className="font-sans text-xs text-bw-danger">
            {errorDetail(create.error, "Couldn't register that. Try again.")}
          </span>
        )}
      </div>
    </form>
  );
}

function runtimeLine(persona: Persona): string | null {
  const runtime = persona.runtime;
  if (!runtime) return null;
  const parts = [runtime.model, runtime.harness, runtime.harness_version].filter(
    (part): part is string => typeof part === "string" && part.length > 0,
  );
  return parts.length ? parts.join(" · ") : null;
}

interface DeclaredSource {
  kind: string;
  ref: string;
  summary: string;
}

/** The instruction sources a persona declared, each with its own summary.
 *  Read defensively: ``runtime`` is the voice's own JSON. */
function declaredSources(persona: Persona): DeclaredSource[] {
  const raw = persona.runtime?.instructions;
  if (!Array.isArray(raw)) return [];
  return raw.filter(
    (item): item is DeclaredSource =>
      typeof item === "object" &&
      item !== null &&
      typeof (item as DeclaredSource).kind === "string" &&
      typeof (item as DeclaredSource).ref === "string" &&
      typeof (item as DeclaredSource).summary === "string",
  );
}

function PersonaRow({ persona }: { persona: Persona }) {
  const setRetired = useSetPersonaRetired();
  const retired = Boolean(persona.retired_at);
  const runtime = runtimeLine(persona);
  const fingerprint = persona.runtime?.instructions_fingerprint;
  const sources = declaredSources(persona);

  return (
    <div className="flex flex-col gap-1 border-b border-bw-blue-muted/30 py-2 last:border-0">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex min-w-0 flex-col">
          <span className={cn("font-sans text-sm font-semibold text-bw-navy", retired && "line-through opacity-60")}>
            {persona.display_name}{" "}
            <span className="font-normal text-bw-ink-muted">({persona.persona_key})</span>
          </span>
          {persona.description && (
            <span className="font-sans text-xs text-bw-ink-muted">{persona.description}</span>
          )}
          <span className="font-sans text-xs text-bw-ink-muted">
            Voiced by {persona.operator_upn ?? `application ${persona.operator_oid}`} · accountable:{" "}
            {persona.accountable_upn}
          </span>
          <span className="font-sans text-xs text-bw-ink-muted">
            {runtime ? (
              <>
                Last declared: {runtime}
                {typeof fingerprint === "string" && <> · instructions {fingerprint.slice(0, 12)}</>}
                {persona.runtime_fresh ? " (this session)" : " (stale: declares again next session)"} ·
                self-reported
              </>
            ) : (
              "Has not declared a runtime yet."
            )}
          </span>
          {sources.length > 0 && (
            <ul className="mt-1 flex flex-col gap-1">
              {sources.map((source) => (
                <li key={`${source.kind}-${source.ref}`} className="font-sans text-xs text-bw-ink-muted">
                  <span className="font-semibold text-bw-navy">{source.kind.replace(/_/g, " ")}</span> ·{" "}
                  <span className="break-all">{source.ref}</span>: {source.summary}
                </li>
              ))}
            </ul>
          )}
        </div>
        <Button
          variant="ghost"
          size="sm"
          disabled={setRetired.isPending}
          onClick={() => setRetired.mutate({ key: persona.persona_key, retired: !retired })}
        >
          {retired ? <RotateCcw className="h-4 w-4" aria-hidden /> : <Trash2 className="h-4 w-4" aria-hidden />}
          {retired ? "Restore" : "Retire"}
        </Button>
      </div>
    </div>
  );
}

export function AdminPersonas() {
  const { data, isLoading, isError } = usePersonas();

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-base">
          <Bot className="h-4 w-4 text-bw-blue" aria-hidden />
          Personas
        </CardTitle>
        <p className="font-sans text-xs text-bw-ink-muted">
          Synthetic voices, AI agents chiefly, that answer the survey without voting: what they file is shown
          beside the team positions, never counted in them. One account can voice several. Each declares
          what it runs on at the start of every session, and every answer says where its knowledge came
          from.
        </p>
      </CardHeader>
      <CardContent className="flex flex-col gap-4 pt-0">
        <RegisterPersona />
        {isLoading && <p className="font-sans text-sm text-bw-ink-muted">Loading personas…</p>}
        {isError && <p className="font-sans text-sm text-bw-ink-muted">Couldn't load personas.</p>}
        {data && data.length === 0 && <p className="font-sans text-xs text-bw-ink-muted">None registered yet.</p>}
        {data && data.length > 0 && (
          <div className="flex flex-col">
            {data.map((p) => (
              <PersonaRow key={p.persona_key} persona={p} />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
